import React, { useState } from 'react';
import { Constants } from 'utils';
import { CommentIcon, ViewIcon, UploadIcon, DownloadIcon, ThreePointsIcon } from 'assets/icons';
import { WhiteImage } from 'assets/img';
import moment from 'moment';
import PaperClip from './paperclip';
import { getRevisionRowData } from './revision-shared';

interface Props {
  item: any;
  role: 'client' | 'lawyer';
  onOpenFile: (key: string) => void;
  onSendResponse: (item: any, file: any) => void;
  onOpenComments: (item: any) => void;
  // Solo se usa en la vista del cliente: abrir el modal de subida cuando
  // todavia no existe un archivo original para la revision.
  onOpenUpload?: (item: any) => void;
  // Solo se usa en la vista del abogado.
  onFinishRevision?: (item: any) => void;
  // Solo se usa en la vista del cliente.
  onSetApproved?: (item: any, response: number) => void;
  // Solo se usa en la vista del cliente: abrir el modal de calificacion
  // cuando el abogado ya marco la revision como finalizada.
  onRate?: (item: any) => void;
  // Cliente o abogado externo asignado: abrir el modal de motivo de cancelación.
  onCancel?: (item: any) => void;
}

// Fila de tabla compartida entre la pantalla de Revision Documental del
// cliente (src/screens/client/revision/revision.tsx) y la del abogado
// (src/screens/lawyer/revision/revision.tsx). Extraida tal cual del
// markup/logica que ya existia duplicado en ambas pantallas, sin cambios
// de comportamiento ni de estilos (eso se aborda en una siguiente etapa).
const RevisionTableRow = ({
  item: i,
  role,
  onOpenFile,
  onSendResponse,
  onOpenComments,
  onOpenUpload,
  onFinishRevision,
  onSetApproved,
  onRate,
  onCancel,
}: Props) => {
  const {
    is_client,
    counterpart,
    counterpartLabel,
    noCounterpartText,
    statusText,
    color,
    showFileActions,
    showComments,
    showFinishButton,
    showAcceptReject,
    showRateButton,
    showCancelButton,
  } = getRevisionRowData(i, role);

  const [menuOpen, setMenuOpen] = useState(false);

  const fileResponseContent = (
    <div className={`file ${is_client && i.file_response == null ? 'disabled' : ''}`}>
      <img src={!is_client ? UploadIcon : ViewIcon} />
      <div style={{ flex: 1 }}>
        <p>
          {!is_client
            ? i.file_response
              ? 'Cambiar documento'
              : 'Subir documento'
            : 'Documento recibido'}
        </p>
      </div>
    </div>
  );

  return (
    <tr className='revision-table-row'>
      <td style={{ width: '20%', position: 'relative' }}>
        {showCancelButton && (
          <div className='kebab-container'>
            <img
              src={ThreePointsIcon}
              className='kebab-btn'
              onClick={() => setMenuOpen((prev) => !prev)}
            />
            {menuOpen && (
              <>
                <div className='kebab-backdrop' onClick={() => setMenuOpen(false)} />
                <div className='kebab-dropdown'>
                  <p
                    onClick={() => {
                      setMenuOpen(false);
                      onCancel?.(i);
                    }}
                  >
                    Cancelar
                  </p>
                </div>
              </>
            )}
          </div>
        )}
        <div className='container-date'>
          <div className='date'>
            <p>{moment(i.created_at).format('DD/MM/YYYY')}</p>
          </div>
          <div className='code'>
            <p>ID {i.number}</p>
          </div>
        </div>
        {counterpart != null && (
          <div className='container-lawyer'>
            <img
              src={counterpart?.photo ? Constants.BASE_STORAGE + counterpart.photo : WhiteImage}
            />
            <div className='container-name'>
              <p className='bold'>{counterpartLabel}</p>
              <p>{counterpart?.full_name}</p>
            </div>
          </div>
        )}
        {counterpart == null && (
          <div className='container-no-lawyer'>
            <p>{noCounterpartText}</p>
          </div>
        )}
      </td>
      <td>
        {!is_client && i?.internal_assignament && (
          <p className='assign-name'>Asignado por legalmente</p>
        )}
        <p className='area-name'>{i.area?.name}</p>
        <p className='document-name'>{i.document?.name}</p>
      </td>
      <td>
        <div className='container-status' style={{ backgroundColor: color }}>
          <p>{statusText}</p>
        </div>
      </td>

      {showComments ? (
        <td className='text-right'>
          <div className='comments' onClick={() => onOpenComments(i)}>
            <img src={CommentIcon} alt='comentario' />
            <p>Chat</p>
          </div>
        </td>
      ) : (
        <td></td>
      )}
      <td className='text-right'>
        {showFileActions && (
          <div className='container-files'>
            <div>
              <button
                type='button'
                className={!is_client ? `file ${i.file == null ? 'disabled' : ''}` : 'file'}
                disabled={!is_client ? !i.file : false}
                onClick={() => {
                  if (i.file) {
                    onOpenFile(i.file);
                  } else if (is_client) {
                    onOpenUpload?.(i);
                  }
                }}
              >
                <img src={!is_client ? DownloadIcon : i.file ? ViewIcon : UploadIcon} />
                <div style={{ flex: 1 }}>
                  <p>
                    {!is_client
                      ? 'Descargar documento'
                      : i.file
                        ? 'Documento enviado'
                        : 'Enviar documento'}
                  </p>
                </div>
              </button>
            </div>
            <div>
              <PaperClip
                formats={['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'png', 'jpg', 'jpeg']}
                value={''}
                onClick={
                  is_client && i.file_response != null ? () => onOpenFile(i.file_response) : null
                }
                disabled={is_client && i.file_response == null}
                onChange={(e: any) => onSendResponse(i, e.target.value)}
              >
                {is_client ? <a>{fileResponseContent}</a> : fileResponseContent}
              </PaperClip>
            </div>
          </div>
        )}
      </td>
      <td>
        {/* Abogado: botón Culminar (cuando está pendiente y hay archivo) */}
        {showFinishButton && (
          <div className='btn-finished' onClick={() => onFinishRevision?.(i)}>
            <p>Culminar</p>
          </div>
        )}
        {/* DESHABILITADO: nuevo flujo de revision documental -- el cliente
            ya no aprueba/rechaza el archivo. Se conserva comentado (no se
            elimina) por si se requiere revertir. */}
        {/* Cliente: botones Aceptar / Rechazar (cuando hay respuesta del abogado) */}
        {/* {showAcceptReject && (
          <div className="row-options">
            <div>
              <button
                className="btn btn-accept"
                onClick={() =>
                  onSetApproved?.(i, Constants.REVISION_STATUS.ACCEPTED)
                }
              >
                Aceptar
              </button>
            </div>
            <div>
              <button
                className="btn btn-reject"
                onClick={() =>
                  onSetApproved?.(i, Constants.REVISION_STATUS.REJECTED)
                }
              >
                Rechazar
              </button>
            </div>
          </div>
        )} */}
        {/* Cliente: botón Calificar (cuando el abogado ya culminó) */}
        {showRateButton && (
          <div className='btn-finished btn-qualify' onClick={() => onRate?.(i)}>
            <p>Calificar</p>
          </div>
        )}
      </td>
    </tr>
  );
};

export default RevisionTableRow;
